import { useEffect, useRef, useState } from "react";
import { FileText, Upload, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";

const DOC_TYPES = [
  { key: "ktp", label: "KTP" },
  { key: "kk", label: "Kartu Keluarga" },
  { key: "passport", label: "Paspor" },
  { key: "photo", label: "Pas Foto" },
];

const STATUS_ICON = { verified: CheckCircle2, pending: Clock, needs_revision: AlertTriangle };
const STATUS_TEXT = { verified: "Terverifikasi", pending: "Menunggu", needs_revision: "Perlu Diperbaiki" };
const STATUS_COLOR = { verified: "text-emerald-600", pending: "text-amber-600", needs_revision: "text-orange-600" };

export default function JamaahDokumen() {
  const [documents, setDocuments] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileInputs = useRef({});

  const load = () => axios.get(`${API}/jamaah/dokumen`).then(({ data }) => setDocuments(data)).catch(() => setDocuments([]));
  useEffect(() => { load(); }, []);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    const formData = new FormData();
    formData.append("doc_type", docType);
    formData.append("file", file);
    try {
      await axios.post(`${API}/uploads/document`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokumen berhasil diunggah");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setUploading(null);
    }
  };

  if (documents === null) return <LoadingScreen />;

  return (
    <div data-testid="jamaah-dokumen-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Dokumen Saya</h1>
        <p className="text-sm text-[#64748B]">Unggah dokumen yang dibutuhkan untuk proses verifikasi.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {DOC_TYPES.map((dt) => {
          const doc = documents.find((d) => d.type === dt.key);
          const Icon = doc ? STATUS_ICON[doc.status] || Clock : FileText;
          return (
            <div key={dt.key} data-testid={`jamaah-document-card-${dt.key}`} className="rounded-2xl bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]">
              <div className="flex items-center justify-between">
                <p className="font-medium text-[#1F2937]">{dt.label}</p>
                {doc && <Icon className={`h-5 w-5 ${STATUS_COLOR[doc.status] || "text-slate-400"}`} />}
              </div>
              <p className="mt-1 text-xs text-[#64748B]">{doc ? STATUS_TEXT[doc.status] : "Belum diunggah"}</p>
              {doc?.admin_note && <p className="mt-2 rounded-lg bg-orange-50 p-2 text-xs text-orange-700">{doc.admin_note}</p>}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                ref={(el) => (fileInputs.current[dt.key] = el)}
                onChange={(e) => handleUpload(dt.key, e.target.files[0])}
                className="hidden"
                data-testid={`jamaah-document-file-input-${dt.key}`}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={uploading === dt.key}
                data-testid={`jamaah-document-upload-button-${dt.key}`}
                onClick={() => fileInputs.current[dt.key]?.click()}
                className="mt-4 gap-2 rounded-full"
              >
                <Upload className="h-3.5 w-3.5" /> {uploading === dt.key ? "Mengunggah..." : doc ? "Unggah Ulang" : "Unggah Dokumen"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
